import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  EVBox,
  EVSvgIcon,
  EVIconButton,
  EVNotificationsNone,
  EVBadge,
  EVHelpOutline,
  EVLink,
} from '@eagleview/ev-comp-library';
import clsx from 'clsx';
import NotificationPopup from 'features/NotificationsPopup';
import { IS_NOTIFS_TRANSPORT_SERVICE_ENABLED, EV_SUPPORT_LINK } from 'constants/AppConstants';
import UserProfile from 'commonComponents/UserProfile';
import ViewProfileDialog from 'features/view-profile-dialog';
import useStyles from './HeaderStyle';
import EVLogoIcon from './EVLogo.Icon';

const Header = (props) => {
  const {
    msgsCount, userDetails, show, userProfileError, isGuestUser,
  } = props;

  const classes = useStyles({ show });

  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [hideNotificationsBadge, setHideNotificationsBadge] = useState(true);
  const [viewProfile, setViewProfile] = useState(false);

  useEffect(() => {
    if (IS_NOTIFS_TRANSPORT_SERVICE_ENABLED === 'YES') {
      setHideNotificationsBadge(msgsCount <= 0);
    }
  }, [msgsCount]);

  /**
   * hides the notifications-panel when clicked outside it
   */
  const handleNotificationClickAway = () => {
    setShowNotificationPanel(false);
  };

  const handleHelpClick = useCallback(() => window.open(EV_SUPPORT_LINK), []);

  return (
    <EVBox className={classes.headerContainer}>
      <EVBox className={classes.evLogo}>
        <EVSvgIcon component={EVLogoIcon} viewBox="0 0 244 244" />
      </EVBox>
      <EVBox className={clsx(classes.headerActionButtons)}>
        {window.location.pathname === '/start'
          && (
            <EVLink id="helpLink" className={classes.helpIcon} onClick={handleHelpClick}>
              <EVHelpOutline />
            </EVLink>
          )}
        {!isGuestUser && (
          <EVIconButton
            id="bellIconButton"
            className={clsx(classes.notification, { [classes.iconButtonClicked]: showNotificationPanel })}
            onClick={() => {
              setShowNotificationPanel(true);
            }}
          >
            <EVBadge id="badgeIcon" variant="dot" color="secondary" invisible={hideNotificationsBadge}>
              <EVNotificationsNone className={clsx({ [classes.selectedIcon]: showNotificationPanel })} />
            </EVBadge>
          </EVIconButton>
        )}
        {!isGuestUser && showNotificationPanel
          && (IS_NOTIFS_TRANSPORT_SERVICE_ENABLED === 'YES')
          && <NotificationPopup id="notificationPanel" handleClickAway={handleNotificationClickAway} />}
        <UserProfile
          setViewProfile={setViewProfile}
          monogramVariant="small"
          userDetails={userDetails}
          withUserMenu
          userProfileError={userProfileError}
          isGuestUser={isGuestUser}
        />
        <ViewProfileDialog userDetails={userDetails} viewProfile={viewProfile} setViewProfile={setViewProfile} />
      </EVBox>
    </EVBox>
  );
};

Header.propTypes = {
  /**
   * No of unread notifications
   * @property {Object} msgsCount
   */
  msgsCount: PropTypes.number,
  /**
   * Details of the logged in user
   * @property {Object} userDetails
   */
  userDetails: PropTypes.shape().isRequired,
  /**
   * show/hide
   * @property {Object} userDetails
   */
  show: PropTypes.bool.isRequired,
  /**
   * Indicates whether there's a user profile error
   * @property {bool} userProfileError
   */
  userProfileError: PropTypes.bool,

  /**
   * Guest user (isResourceShared && !isEvUser); hide User Settings, logout → /signin?redirect. #genai
   * @property {bool} isGuestUser
   */
  isGuestUser: PropTypes.bool,
};

Header.defaultProps = {
  msgsCount: 0,
  userProfileError: false,
  isGuestUser: false,
};

const selector = (state) => {
  const { isResourceShared, isEvUser } = state.GuestAccess || {};
  const isShareTokenFromState = Boolean(state.Authorize?.isShareToken);
  // Guest = shared-resource non-EV user OR current token is SHARE (from Redux, not localStorage). #genai
  const isGuestUser = Boolean((isResourceShared && !isEvUser) || isShareTokenFromState);
  return {
    msgsCount: state.SideBar.msgsCount,
    userDetails: state.SideBar.userDetails,
    userProfileError: state.SideBar.userProfileError,
    isGuestUser,
  };
};

export default connect(selector)(Header);
